import { Fragment, SyntheticEvent, useEffect, useMemo, useRef, useState } from "react";
import { useSnackbar } from "notistack";

// MUI
import {
  Box,
  Collapse,
  Divider,
  Grid,
  Link,
  SelectChangeEvent,
  Typography,
  createFilterOptions,
  useMediaQuery,
} from "@mui/material";
import { Theme } from "@mui/material/styles";

// Project import
import SingleColumnInput from "components/input/singleColumnInput";
import { useTranslation } from "utils/locales/utilityFunctions";
import AutoCompleteGrouped from "components/input/autoCompleteGrouped";
import {
  ActivityBiomassScreening,
  ActivityResponsible,
  FirestoreActivityProps,
} from "types/firestoreFeatures";
import useMapData from "hooks/useMapData";
import useActivityData from "hooks/useActivityData";
import { getEstateDataAsStr, getUserOrgDataAsStr, validateEmail } from "utils/textModification";
import { ContactType } from "components/shareData/shareDialog";
import useAuth from "hooks/useAuth";
import getContactOptions from "components/estatesAndMapSelecter/getContactOptions";
import { TeamMember } from "types/auth";
import {
  DEFAULT_ACTIVITY_STATUS_TYPE,
  DEFAULT_ACTIVITY_STATUS,
  DEFAULT_SUPPORTED_FILE_TYPES,
  DEFAULT_SUPPORTED_FILE_SIZE,
} from "config/config-general";
import useEstatesAndMaps from "hooks/useEstatesAndMaps";
import ProjectChooseOrCreate from "./projectChooseOrCreate";
import dayjs from "dayjs";
import { SaveActivityProps } from "types/mapData";
import useAppFunctionalityConfig from "hooks/useAppFunctionalityConfig";
import { ActivityThinningObject } from "types/stand";
import TabPanel from "components/navigation/TabPanel";
import { useFilePicker } from "use-file-picker";
import {
  FileAmountLimitValidator,
  FileSizeValidator,
  FileTypeValidator,
} from "use-file-picker/validators";
import {
  filePickerErrorInterpreter,
  getRootFolderAndRootIdFromUploadFileProps,
} from "features/files/utils/fileUtilities";

import { FirestoreFileProps, UploadFileEntryProps } from "types/files";
import config from "config/config";
import useFiles from "hooks/useFiles";
import ConfirmDialog from "components/dialogs/ConfirmDialog";
import { snackbarChecker } from "./utils/snackbarHelperFunctions";
import {
  getActivitySelectionOptions,
  getEstateDataFromProjectId,
  getProjectDataFromProjectId,
  getReferencesFromSelectedFeature,
} from "./utils/activityFormData";
import {
  createActivityFormConfig,
  createActivityFormSections,
  getActivityInformationElements,
  getInitialFullBiomassScreeningEnabled,
} from "./utils/activityFormConfig";
import ActivityFormTabs from "./components/activityFormTabs";
import ActivityBasicInformationFields from "./components/activityBasicInformationFields";
import ActivityAssignmentAndStatusFields from "./components/activityAssignmentAndStatusFields";
import ActivityReferencesSection from "./components/activityReferencesSection";
import ActivityScreeningSection from "./components/activityScreeningSection";
import ActivityScreeningActivationFields from "./components/activityScreeningActivationFields";
import BiomassScreeningCoreFields from "./components/biomassScreeningCoreFields";
import BiomassScreeningCertificateFields, {
  hasBiomassScreeningCertificateFields,
  hasBiomassScreeningCertificationNumber,
} from "./components/biomassScreeningCertificateFields";
import BiomassScreeningInterventionField from "./components/biomassScreeningInterventionField";
import RegenerationAgreementField from "./components/regenerationAgreementField";
import ToolbarDivider from "components/display/toolbarDivider";
import {
  BIOMASS_CATEGORIES,
  hasClearingIntervention,
  hasClearcutLikeIntervention,
  hasFellingAndThinningIntervention,
  hasThinningLikeIntervention,
  shouldShowRegenerationAgreement,
} from "./options";
import { generateUUID } from "utils/deviceTools";
import { getEmailLanguageFromUserOrg } from "utils/databaseUtils";

interface ActivityChooseOrCreateProps {
  openExt: boolean;
  setOpenExt: (open: boolean) => void;
  existingActivity?: FirestoreActivityProps;
  withProjectCreate?: boolean;
  withActivityCreate?: boolean;
  orientationFeature?: boolean;
  withDocking?: boolean;
  onActivityCreated?: (activityId: string) => void;
}

const filterContacts = createFilterOptions<ContactType>();

const ActivityChooseOrCreate = ({
  openExt,
  withActivityCreate,
  withProjectCreate,
  existingActivity,
  orientationFeature,
  withDocking,
  onActivityCreated,
  setOpenExt,
}: ActivityChooseOrCreateProps) => {
  const style = {
    "&.MuiTab-root": {
      "&:hover": {
        bgcolor: "secondary.light",
        height: 20,
      },
    },
    "&.Mui-selected": {
      bgcolor: "primary.lighter",
      "&:hover": {
        bgcolor: "primary.lighter",
        height: 30,
      },
    },
    textTransform: "inherit",
  };

  // --- Hooks ---  //
  const { translate } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const appConfig = useAppFunctionalityConfig().config.activityData;
  const activityConfig = useMemo(() => createActivityFormConfig(appConfig), [appConfig]);
  const { user, userOrg, orgTeam, orgContacts, activityTypes } = useAuth();
  const { selectedProjectId, estates, projects, shareProjectActivity, setSelectedProjectId } =
    useEstatesAndMaps();
  const { projects: projectFiles, uploadFiles, downloadFile, deleteFile, fetchFiles } = useFiles();
  const {
    selectedActivityId,
    activities,
    selectedFeature,
    standData,
    thinningData,
    activityFeatures,
    setExternallyActivatedThemes,
    saveActivity,
    setSelectedActivityId,
    saveFeature,
    setMetaDataEditMode,
    deleteThinning,
  } = useMapData();
  const { getActivityTypeList, getUserProjectRole } = useActivityData();

  const matchDownSM = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));

  // --- state --- //
  const [dataChanged, setDataChanged] = useState<boolean>(false);
  const [options, setOptions] = useState<any[]>([]);
  const [openChooseProject, setOpenChooseProject] = useState<boolean>(false);
  const [chosenActivity, setChosenActivity] = useState<any>([]);
  const [teamList, setTeamList] = useState<ContactType[] | null>(null);
  const [contactList, setContactList] = useState<ContactType[] | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [fileError, setFileError] = useState<string | null>(null);
  const [openFilePickerModal, setOpenFilePickerModal] = useState<boolean>(false);
  const [openFileConfirmDialog, setOpenFileConfirmDialog] = useState<boolean>(false);
  const [openCancelConfirmDialog, setOpenCancelConfirmDialog] = useState<boolean>(false);

  const [existingActivitySectionOpen, setExistingActivitySectionOpen] = useState(false);
  const [newActivitySectionOpen, setNewActivitySectionOpen] = useState(false);
  const [newActivityTitle, setNewActivityTitle] = useState("");
  const [newActivityDescription, setNewActivityDescription] = useState("");
  const [newActivityOwnerName, setNewActivityOwnerName] = useState("");
  const [newActivityRefs, setNewActivityRefs] = useState<{ id: string; label: string }[]>([]);
  const [newActivityType, setNewActivityType] = useState<any>([]);
  const [newActivityResponsible, setNewActivityResponsible] = useState<ContactType[]>([]);
  const [newActivitySendToResponsible, setNewActivitySendToResponsible] = useState<boolean>(false);
  const [newActivityExpireDate, setNewActivityExpireDate] = useState<Date | null>(null);
  const [newActivityStatus, setNewActivityStatus] = useState<DEFAULT_ACTIVITY_STATUS_TYPE>(
    DEFAULT_ACTIVITY_STATUS[0],
  );

  const [biomassScreeningData, setBiomassScreeningData] =
    useState<ActivityBiomassScreening>(undefined);
  const [fullBiomassScreeningEnabled, setFullBiomassScreeningEnabled] = useState(false);
  const [eudrReference, setEudrReference] = useState("");

  const [removedStandIds, setRemovedStandIds] = useState<string[]>([]);

  const [newFiles, setNewFiles] = useState<{ file: File; metaData?: any }[]>([]);
  const [loadedThinningData, setLoadedThinningData] = useState<boolean>(false);
  const [loadedBiomassData, setLoadedBiomassData] = useState<boolean>(false);

  // --- Refs --- //
  const groupByRef = useRef(false);
  const updatedThinningDataRef = useRef<ActivityThinningObject[]>([]);
  const selectedActivityEstateDataRef = useRef<any>(null);
  const derivedActivityDataRef = useRef<{ totalArea: number; largestArea: number } | undefined>(
    undefined,
  );

  const selectedFileDocRef = useRef<FirestoreFileProps | null>(null);
  const deleteFileRef = useRef<FirestoreFileProps | null>(null);
  const fileCategoryRef = useRef<string[]>([]);

  // --- State dependent hooks --- //
  const activitySpecificFiles = useMemo(() => {
    // Find project id
    if (!existingActivity) return [];
    const { projectId, id } = existingActivity;
    if (!projectId || !id) return [];
    if (!projectFiles[projectId]) {
      // If no files for project, fetch files
      fetchFiles({ rootFolder: "projects", rootId: projectId });
    }
    if (!projectFiles[projectId]) return [];
    const actFiles = Object.values(projectFiles[projectId]).filter(
      (el) => el.activityId === id && el.categories && el.categories.includes("screening"),
    );
    return actFiles.sort(
      (a, b) => (Date.parse(b.creationDate || "") || 0) - (Date.parse(a.creationDate || "") || 0),
    );
  }, [projectFiles, existingActivity]);

  const { openFilePicker } = useFilePicker({
    multiple: false,
    accept: DEFAULT_SUPPORTED_FILE_TYPES.map((el) => `.${el}`),
    readAs: "DataURL",
    validators: [
      new FileAmountLimitValidator({ max: 3 }),
      new FileTypeValidator(DEFAULT_SUPPORTED_FILE_TYPES),
      new FileSizeValidator({ maxFileSize: DEFAULT_SUPPORTED_FILE_SIZE }),
    ],
    onFilesSelected: (data) => handleFilesChange(data),
  });

  useEffect(() => {
    if (openFilePickerModal) {
      openFilePicker();
      setOpenFilePickerModal(false);
    }
  }, [openFilePickerModal]);

  // --- Data --- //
  const ACTIVITY_TYPES = useMemo(() => {
    return getActivityTypeList().map((el) => {
      return {
        ...el,
        groupName: translate(`activity-group-${el.group}-label`),
      };
    });
  }, [translate("render-change"), activityTypes]);

  const ACTIVITY_STATUS = useMemo(() => {
    return DEFAULT_ACTIVITY_STATUS.map((el) => {
      return {
        id: el,
        label: translate(`activity-status-${el}`),
      };
    });
  }, []);

  const TEAM_DATA: ContactType[] = teamList ? (teamList as ContactType[]) : [];
  const CONTACTS_DATA: ContactType[] = contactList ? (contactList as ContactType[]) : [];
  const RESPONSIBLE_DATA = [...TEAM_DATA, ...CONTACTS_DATA];

  // --- Effects --- //
  // Handle unmount
  useEffect(() => {
    return () => {
      setMetaDataEditMode(false);
    };
  }, []);

  // Check for referenced stand thinning when activity refs change
  const hasReferencedStandThinning = useMemo(() => {
    if (updatedThinningDataRef.current.length > 0) return true;
    if (newActivityRefs.length === 0) return false;

    for (const ref of newActivityRefs) {
      const [mapId, featureId] = ref.id.split("/");
      if (!mapId || !featureId) continue;

      const stands = standData?.[mapId];
      if (!stands || stands.length === 0) continue;

      const hasStand = stands.some((s: any) => {
        if (s.featureId !== featureId) return false;
        if (removedStandIds?.includes(s.id)) return false;
        return true;
      });

      if (hasStand) return true;
    }
    return false;
  }, [newActivityRefs, standData, removedStandIds, updatedThinningDataRef.current.length]);

  // If responsible is hidden and no responsible set, set current user as responsible
  useEffect(() => {
    if (
      RESPONSIBLE_DATA.length > 0 &&
      !activityConfig.isVisible("responsible") &&
      newActivityResponsible.length === 0
    ) {
      // Find current user in list
      const currentUser = RESPONSIBLE_DATA.find((el) => el.id === user?.id);
      if (currentUser) {
        setNewActivityResponsible([currentUser]);
      }
    }
  }, [RESPONSIBLE_DATA.length, activityConfig, newActivityResponsible.length, user?.id]);

  // If activity type is hidden and no type set, set default type
  useEffect(() => {
    if (
      !activityConfig.isVisible("type") &&
      activityConfig.defaultTypeId &&
      newActivityType.length === 0
    ) {
      // Find default type in list
      let defaultType = ACTIVITY_TYPES.find((el) => el.id === activityConfig.defaultTypeId);
      if (!defaultType) {
        defaultType = ACTIVITY_TYPES.find((el) => el.id === "MechanicalLogging");
      }
      if (defaultType) {
        setNewActivityType([defaultType]);
      }
    }
  }, [activityConfig, newActivityType, ACTIVITY_TYPES]);

  useEffect(() => {
    if (openExt && existingActivity) {
      setStateWithExistingData(existingActivity);
      setNewActivitySectionOpen(true);
    }
  }, [existingActivity, openExt]);

  useEffect(() => {
    if (chosenActivity.length === 0) return;
    setNewActivitySectionOpen(false);
    // Find activity data
    const { projectId, activityId } = chosenActivity[0];
    if (!activities[projectId] || !activities[projectId][activityId]) return;
    const activityData = activities[projectId][activityId];
    // Set all relevent state accoridng to chosen activity data
    setStateWithExistingData(activityData);
    setExistingActivitySectionOpen(true);
  }, [chosenActivity]);

  useEffect(() => {
    if (selectedProjectId && activities[selectedProjectId]) {
      const newOptions = getActivitySelectionOptions(Object.values(activities[selectedProjectId]));
      setOptions(newOptions);
    } else {
      setOptions([]);
    }
  }, [selectedProjectId, activities]);

  useEffect(() => {
    if (selectedFeature && newActivityRefs.length === 0 && openExt && !orientationFeature) {
      const refArr = getReferencesFromSelectedFeature(selectedFeature);
      if (refArr.length > 0) {
        setNewActivityRefs(refArr);
      }
    }
  }, [selectedFeature, openExt]);

  useEffect(() => {
    if (orgTeam) {
      const newList = Object.entries(orgTeam).map(([id, el]: [string, TeamMember]) => {
        const name = el.name && el.name !== "" ? el.name : el.email;
        return {
          ...el,
          id: id,
          name: name,
          label: name + ":" + el.email,
          personType: "team" as any,
          personTypeLabelText: translate("team"),
        };
      });
      setTeamList(newList);
    }
  }, [orgTeam]);

  useEffect(() => {
    if (orgContacts) {
      const newList = getContactOptions({
        userId: user ? user.id! : "",
        contacts: orgContacts,
        selectedData: null,
        orgMapIds: [],
        orgProjectIds: [],
        personTypeLabelText: translate("contacts"),
      });
      setContactList(newList);
    }
  }, [orgContacts]);

  useEffect(() => {
    if (openExt && !selectedProjectId) {
      setOpenChooseProject(true);
    } else if (openExt && selectedProjectId) {
      setOpenChooseProject(false);
    } else if (!openExt && !selectedProjectId) {
      setOpenChooseProject(false);
    }
  }, [openExt, selectedProjectId]);

  // Dynamic variables:
  const requireExpireDate =
    newActivityType.length > 0 &&
    newActivityType[0].group === "logging" &&
    hasReferencedStandThinning;

  // --- Functions --- //
  function handleFilesChange(data: any) {
    // Error handling
    if (data.errors) {
      filePickerErrorHandler(data.errors);
      return;
    }
    setFileError(null);
    // Handle new files
    if (!selectedFileDocRef.current) {
      const newFilesPlusMeta = data.plainFiles.map((file: File) => {
        return {
          file,
          metaData: {
            categories: ["project", "activity", ...fileCategoryRef.current],
          },
        };
      });
      const updatedFiles = [...newFiles, ...newFilesPlusMeta];
      if (newFilesPlusMeta.length > 0) setDataChanged(true);
      setNewFiles(updatedFiles);
      return;
    }
    // For files meta data, Check if selected file
    const id = selectedFileDocRef.current.id;
    const categories = selectedFileDocRef.current.categories || [
      "project",
      "activity",
      ...fileCategoryRef.current,
    ];
    const orgId = selectedFileDocRef.current.orgId || null;
    const estateId = selectedFileDocRef.current.estateId || null;

    const mapId = selectedFileDocRef.current.mapId || null;
    const projectId = selectedFileDocRef.current.projectId || selectedProjectId || null;
    const activityId = selectedFileDocRef.current.activityId || null;

    const featureIds = selectedFileDocRef.current.featureIds || [];
    const featureCategories = selectedFileDocRef.current.featureCategories || [];

    const featureRefLabels = selectedFileDocRef.current.featureRefLabels || [];

    const fileData: UploadFileEntryProps = {
      id: id,
      bucket: config.DEFAULT_FILES_BUCKET,
      categories: categories,
      uploadedBy: {
        id: user?.id,
        name: user?.firstName + " " + user?.lastName,
        email: user?.email,
        orgId: user?.orgId,
      },
      orgId: orgId,
      estateId: estateId,
      mapId: mapId,
      projectId: projectId,
      activityId: activityId,
      featureIds: featureIds,
      featureCategories: featureCategories,
      featureRefLabels: featureRefLabels,
    };
    // Find root folder and root id
    const root = getRootFolderAndRootIdFromUploadFileProps(fileData);
    if (!root) {
      setFileError(translate("file-upload-error-generic"));
      return;
    }
    // Upload files
    uploadFiles({
      fileDoc: fileData,
      files: data.plainFiles,
      bucket: config.DEFAULT_FILES_BUCKET,
      rootFolder: root.rootFolder,
      rootId: root.rootId,
    });
    enqueueSnackbar(translate("file-upload-success"), {
      variant: "success",
    });
  }

  function handleOpenFilePicker(state: boolean, categories?: string[]) {
    selectedFileDocRef.current = null;
    fileCategoryRef.current = categories || [];
    setOpenFilePickerModal(true);
  }

  function handleChangeFile(file: FirestoreFileProps) {
    selectedFileDocRef.current = file;
    setFileError(null);
    setOpenFilePickerModal(true);
  }

  function handleDownloadFile(fileUrl: string, fileName?: string) {
    downloadFile(fileUrl, fileName);
  }

  function handleDeleteFile(file: FirestoreFileProps) {
    setFileError(null);
    const root = getRootFolderAndRootIdFromUploadFileProps(file);
    if (!root || !file.id) {
      setFileError(translate("file-delete-error"));
      return;
    }
    deleteFile({
      fileDocId: file.id,
      bucket: file.bucket,
      rootFolder: root.rootFolder,
      rootId: root.rootId,
      fileName: file.name,
    });
  }

  function handleDeleteNewFile(index: number) {
    // Remove the file from the newFiles array
    const updatedFiles = [...newFiles];
    updatedFiles.splice(index, 1);
    setNewFiles(updatedFiles);
  }

  const handleOpenFileConfirmDialog = (file: FirestoreFileProps) => {
    deleteFileRef.current = file;
    setOpenFileConfirmDialog(true);
  };

  const handleCancelFileConfirmDialog = () => {
    deleteFileRef.current = null;
    setOpenFileConfirmDialog(false);
  };

  const handleConfirmFileConfirmDialog = () => {
    if (deleteFileRef.current) {
      handleDeleteFile(deleteFileRef.current);
    }
    deleteFileRef.current = null;
    setOpenFileConfirmDialog(false);
  };

  const handleCloseInput = () => {
    if (dataChanged) {
      setOpenCancelConfirmDialog(true);
    } else {
      handleClose();
    }
  };

  const handleConfirmDialog = () => {
    handleClose();
  };

  const handleCancelConfirmDialog = () => {
    setOpenCancelConfirmDialog(false);
  };

  function filePickerErrorHandler(errors: any) {
    const errorStr = filePickerErrorInterpreter(errors, translate);
    if (errorStr) setFileError(errorStr);
  }

  const resetState = () => {
    setDataChanged(false);
    setOpenCancelConfirmDialog(false);
    setNewActivityTitle("");
    setNewActivityDescription("");
    setNewActivityOwnerName("");
    setNewActivityType([]);
    setNewActivityExpireDate(null);
    setNewActivityStatus(DEFAULT_ACTIVITY_STATUS[0]);
    setNewActivityRefs([]);
    setNewActivityResponsible([]);
    setNewActivitySendToResponsible(false);
    setExistingActivitySectionOpen(false);
    setNewActivitySectionOpen(false);
    setChosenActivity([]);
    setSubmitError(null);
    setTabValue(0);
    setRemovedStandIds([]);
    setNewFiles([]);
    setBiomassScreeningData(undefined);
    setFullBiomassScreeningEnabled(false);
    setEudrReference("");
    updatedThinningDataRef.current = [];
    selectedFileDocRef.current = null;
    deleteFileRef.current = null;
    fileCategoryRef.current = [];
    derivedActivityDataRef.current = undefined;
  };

  const setStateWithExistingData = (existingActivity?: FirestoreActivityProps) => {
    if (!existingActivity) {
      setNewActivityTitle("");
      setNewActivityDescription("");
      setNewActivityOwnerName("");
      setNewActivityType([]);
      setNewActivityExpireDate(null);
      setNewActivityStatus(DEFAULT_ACTIVITY_STATUS[0]);
      setNewActivityResponsible([]);
      setNewActivitySendToResponsible(false);
      setBiomassScreeningData(undefined);
      setFullBiomassScreeningEnabled(false);
      setEudrReference("");
      derivedActivityDataRef.current = undefined;
      selectedFileDocRef.current = null;
      deleteFileRef.current = null;
      fileCategoryRef.current = [];
      return;
    }
    setSelectedProjectId(existingActivity.projectId);
    setSelectedActivityId(existingActivity.id);
    setNewActivityTitle(existingActivity.name || "");
    setNewActivityDescription(existingActivity.description || "");
    setNewActivityOwnerName(
      existingActivity.ownerInformation && existingActivity.ownerInformation.ownerName
        ? existingActivity.ownerInformation.ownerName
        : "",
    );
    setNewActivityType(existingActivity.type ? [existingActivity.type] : []);
    setNewActivityExpireDate(
      existingActivity.expireDate ? dayjs(existingActivity.expireDate).toDate() : null,
    );

    setRemovedStandIds(existingActivity.removedStandIds || []);
    setNewActivityStatus(existingActivity.activityStatus || DEFAULT_ACTIVITY_STATUS[0]);
    setFullBiomassScreeningEnabled(
      getInitialFullBiomassScreeningEnabled({
        screeningToggleEnabled: activityConfig.isEnabled("infoBiomassScreeningEnableScreening"),
        riskAssessment: existingActivity.biomassScreening?.riscAssessment,
        fullBiomassScreeningEnabled: existingActivity.fullBiomassScreeningEnabled,
        hasBiomassScreening: !!existingActivity.biomassScreening,
      }),
    );
    setEudrReference(existingActivity.eudrReference || "");

    let newRefs: { id: string; label: string }[] = [];
    if (existingActivity.mapFeatureRefs && existingActivity.mapFeatureRefsLabel) {
      newRefs = newActivityRefs;
      if (selectedFeature && !orientationFeature) {
        const refArr = getReferencesFromSelectedFeature(selectedFeature);
        if (refArr.length > 0) newRefs = refArr;
      }
      existingActivity.mapFeatureRefs.forEach((el, i) => {
        if (!newRefs.find((ref) => ref.id === el)) {
          newRefs.push({
            id: el,
            label: existingActivity.mapFeatureRefsLabel![i],
          });
        }
      });
    }
    setNewActivityRefs(newRefs);

    if (existingActivity.responsible) {
      const { id, name, email, released, status, type } = existingActivity.responsible;
      // If no id, check for id in contacts list
      let idFromContacts: string | null | undefined = id;
      if (!idFromContacts && RESPONSIBLE_DATA) {
        const contact = RESPONSIBLE_DATA.find((el) => el.email === email);
        if (contact && contact.personType === "contact") {
          idFromContacts = contact.id;
        }
      }

      setNewActivityResponsible([
        {
          id: idFromContacts ? idFromContacts : undefined,
          name: name ? name : undefined,
          label: name ? name : email ? email : "",
          email: email ? email : undefined,
          personType: type as any,
          status: status ? status : "",
        },
      ]);

      setNewActivitySendToResponsible(released !== undefined ? released : false);
    }
    // Get data from activity features
    let activityFeats = activityFeatures ? activityFeatures[existingActivity.id] : null;
    if (activityFeats) {
      let totalArea = 0;
      let largestArea = 0;
      // Get areas
      Object.values(activityFeats).forEach((el) => {
        if (!["Polygon", "MultiPolygon"].includes(el.geometry.type)) return;
        if (!el.properties?.countInMetrics || !el.properties?.featureMetric) return;
        totalArea += el.properties.featureMetric;
        if (el.properties.featureMetric > largestArea) {
          largestArea = el.properties.featureMetric;
        }
      });
      derivedActivityDataRef.current = { totalArea, largestArea };
    }
    // Set biomass screening data
    if (existingActivity.biomassScreening) {
      // Check for screening category
      const { category } = existingActivity.biomassScreening;
      if (category) {
        if (category === "forest") {
          setExternallyActivatedThemes(["screening-dk-forest"]);
        } else {
          setExternallyActivatedThemes(["screening-dk"]);
        }
      }
      const newBiomassScreeningData: ActivityBiomassScreening = {
        ...existingActivity.biomassScreening,
      };
      if (!newBiomassScreeningData.screeningDate) {
        newBiomassScreeningData.screeningDate = dayjs().format();
      }
      setBiomassScreeningData(newBiomassScreeningData);
    } else {
      setBiomassScreeningData({
        screeningDate: dayjs().format(),
      } as ActivityBiomassScreening);
    }
  };

  const handleClose = () => {
    setOpenCancelConfirmDialog(false);
    setOpenExt(false);
    setLoading(false);
    setIsDisabled(false);
    resetState();
  };

  const handleSignalCancel = () => {
    setOpenChooseProject(false);
    handleClose();
  };

  const handleSave = async () => {
    // Input error handling
    if (!newActivitySectionOpen && chosenActivity.length === 0) {
      setSubmitError("activity-required");
      return;
    }
    if (newActivitySectionOpen && newActivityTitle === "") {
      setSubmitError("activity-title-required");
      return;
    }
    if (
      hasBiomassScreeningCertificateFields(biomassScreeningData) &&
      !hasBiomassScreeningCertificationNumber(biomassScreeningData)
    ) {
      setSubmitError("activity-certificate-number-required");
      return;
    }
    // if (newActivityType.length > 0 && newActivityType[0].group === "logging") {
    if (requireExpireDate && newActivityExpireDate === null) {
      setSubmitError("activity-expire-date-required");
      return;
    }
    // }
    setSubmitError(null);
    setLoading(true);
    // Create correlation id for activity
    const correlationId = generateUUID();
    // Check if activity type supports screening data
    let screeningData = {
      ...biomassScreeningData,
    } as ActivityBiomassScreening | null;
    if (newActivityType[0] && !["logging", "chipping"].includes(newActivityType[0].group)) {
      screeningData = null;
    }
    // Keep certificate values in form state while users switch risk assessment,
    // but never persist them for a non-certified screening.
    if (screeningData?.riscAssessment?.includes("notCertified")) {
      screeningData = {
        ...screeningData,
        certificationNumber: null,
        fscCertificationNumber: null,
        pefcCertificationNumber: null,
      };
    }

    // If existing data just save current activity
    if (existingActivity) {
      // Remember to remove map feature refs as we do not want to update those
      let responsible: ActivityResponsible | null = null;
      // Check if responsible person has been chosen
      if (newActivityResponsible && newActivityResponsible.length > 0) {
        const resp = newActivityResponsible[0];
        responsible = {
          id: resp.id ? resp.id : null,
          name: resp.name ? resp.name : null,
          email: resp.email ? resp.email : null,
          type: resp.personType as "team" | "contact",
          status: resp.status ? resp.status : "invite-pending",
          released: newActivitySendToResponsible,
          notified: existingActivity.responsible?.notified || false,
          givenBy: {
            id: user?.id ? user?.id : "",
            name: user?.name ? user?.name : "",
          },
        };
        // Handle share with responsible
        if (responsible && responsible.released && existingActivity.projectId) {
          // Get langauge based on userOrg
          const lang = getEmailLanguageFromUserOrg(userOrg);
          const result = await shareProjectActivity(
            existingActivity.projectId,
            { id: existingActivity.id, name: newActivityTitle },
            responsible,
            lang,
          );
          if (result && result.data && result.data.contactId) {
            if (responsible.id === null) responsible.id = result.data.contactId;
          }
        }
      }
      // Save activity
      const data: SaveActivityProps = {
        projectId: existingActivity.projectId,
        dontCheckForMapFeatures: true,
        id: existingActivity.id === "new" ? undefined : existingActivity.id,
        thinningData: updatedThinningDataRef.current,
        payload: {
          ...existingActivity,
          name: newActivityTitle,
          description: newActivityDescription,
          ownerInformation: {
            ownerName: newActivityOwnerName,
          },
          type: newActivityType[0] ? newActivityType[0] : null,
          expireDate: newActivityExpireDate ? dayjs(newActivityExpireDate).format() : null,
          activityStatus: newActivityStatus,
          responsible: responsible,
          removedStandIds: removedStandIds,
          biomassScreening: screeningData,
          fullBiomassScreeningEnabled,
          eudrReference,
          correlationId,
        },
      };
      const actId = await saveActivity(data);
      if (actId && actId !== "") {
        // Check for files and save these
        for (const file of newFiles) {
          await saveFiles(actId, existingActivity.projectId!, [file.file], file.metaData);
        }
        if (existingActivity.id === "new") onActivityCreated?.(actId);
      }
      handleClose();
      return;
    }
    // Check if adding to existing activity
    if (chosenActivity.length > 0) {
      const ca = chosenActivity[0];
      let activityData: FirestoreActivityProps | null = null;
      if (activities[ca.projectId] && activities[ca.projectId][ca.activityId]) {
        activityData = activities[ca.projectId][ca.activityId];
      }
      if (!activityData) {
        setSubmitError("activity-not-found");
        setLoading(false);
        setIsDisabled(false);
        return;
      }
      // Check for orientation feature (just add feature to activity no need to update/save activity)
      if (orientationFeature) {
        let payload = { ...selectedFeature.feature };
        payload.properties = {
          ...selectedFeature.feature.properties,
          activityId: activityData!.id,
          originFeature: true,
          status: "active",
          correlationId,
        };
        saveFeature(
          {
            activityId: activityData!.id,
            id: selectedFeature.feature.properties.id,
            payload: payload,
          },
          "activities",
        );
        handleClose();
        return;
      }
      // Check if map feature is already added to activity
      let mapFeatureRefs = activityData?.mapFeatureRefs ? activityData?.mapFeatureRefs : [];
      let mapFeatureRefsLabels = activityData?.mapFeatureRefsLabel
        ? activityData?.mapFeatureRefsLabel
        : [];
      newActivityRefs.forEach((el) => {
        if (!mapFeatureRefs.includes(el.id)) {
          mapFeatureRefs.push(el.id);
          mapFeatureRefsLabels.push(el.label);
        }
      });
      // Create data for saving activity
      // Remember to remove map feature refs as we do not want to update those
      let responsible: ActivityResponsible | null = null;
      // Check if responsible person has been chosen
      if (newActivityResponsible && newActivityResponsible.length > 0) {
        const resp = newActivityResponsible[0];
        responsible = {
          id: resp.id ? resp.id : null,
          name: resp.name ? resp.name : null,
          email: resp.email ? resp.email : null,
          type: resp.personType as "team" | "contact",
          status: resp.status ? resp.status : "invite-pending",
          released: newActivitySendToResponsible,
          notified: activityData.responsible?.notified || false,
          givenBy: {
            id: user?.id ? user?.id : "",
            name: user?.name ? user?.name : "",
          },
        };
        // Handle share with responsible
        if (responsible && responsible.released && activityData.projectId) {
          // Get langauge based on userOrg
          const lang = getEmailLanguageFromUserOrg(userOrg);
          const result = await shareProjectActivity(
            activityData.projectId,
            { id: activityData.id, name: newActivityTitle },
            responsible,
            lang,
          );
          if (result && result.data && result.data.contactId) {
            if (responsible.id === null) responsible.id = result.data.contactId;
          }
        }
      }
      // Save activity
      // const data: SaveActivityProps = {
      //   projectId: activityData.projectId,
      //   dontCheckForMapFeatures: true,
      //   id: activityData.id === 'new' ? undefined : activityData.id,
      //   thinningData: updatedThinningDataRef.current,
      //   payload: {
      //     ...activityData,
      //     name: newActivityTitle,
      //     description: newActivityDescription,
      //     ownerInformation: {
      //       ownerName: newActivityOwnerName,
      //     },
      //     type: newActivityType[0] ? newActivityType[0] : null,
      //     expireDate: newActivityExpireDate
      //       ? dayjs(newActivityExpireDate).format()
      //       : null,
      //     activityStatus: newActivityStatus,
      //     responsible: responsible,
      //     removedStandIds: removedStandIds,
      //     biomassScreening: screeningData,
      //     mapFeatureRefs: mapFeatureRefs,
      //     mapFeatureRefsLabel: mapFeatureRefsLabels,
      //     correlationId,
      //   },
      // }
      // const actId = await saveActivity(data);

      const actId = await saveActivity({
        projectId: selectedProjectId!,
        id: activityData!.id,
        thinningData: updatedThinningDataRef.current,
        // @ts-ignore
        payload: {
          ...activityData,
          correlationId,
          mapFeatureRefs: mapFeatureRefs,
          mapFeatureRefsLabel: mapFeatureRefsLabels,
          expireDate: newActivityExpireDate ? dayjs(newActivityExpireDate).format() : null,
        },
      });
      // Save potential files
      if (actId && actId !== "") {
        // Check for files and save these
        for (const file of newFiles) {
          await saveFiles(actId, selectedProjectId!, [file.file], file.metaData, correlationId);
        }
      }
      handleClose();
      return;
    }
    // Else create new activity
    // Set responsible data if there
    let responsible: ActivityResponsible | null = null;
    // Check if responsible person has been chosen
    if (newActivityResponsible && newActivityResponsible.length > 0) {
      const resp = newActivityResponsible[0];
      responsible = {
        id: resp.id ? resp.id : null,
        name: resp.name ? resp.name : null,
        email: resp.email ? resp.email : null,
        type: resp.personType as "team" | "contact",
        status: resp.status ? resp.status : "invite-pending",
        released: newActivitySendToResponsible,
        notified: false,
        givenBy: {
          id: user?.id ? user?.id : "",
          name: user?.name ? user?.name : "",
        },
      };
      // Handle share with responsible
      if (responsible.released && selectedProjectId) {
        // Get langauge based on userOrg
        const lang = getEmailLanguageFromUserOrg(userOrg);
        const result = await shareProjectActivity(
          selectedProjectId,
          { id: "", name: newActivityTitle },
          responsible,
          lang,
        );
        if (result && result.data && result.data.contactId) {
          if (responsible.id === null) responsible.id = result.data.contactId;
        }
      }
    }

    const actId = await saveActivity({
      projectId: selectedProjectId!,
      id: undefined,
      thinningData: updatedThinningDataRef.current,
      payload: {
        mapFeatureRefs: newActivityRefs.map((el) => el.id),
        mapFeatureRefsLabel: newActivityRefs.map((el) => el.label),
        removedStandIds: removedStandIds,
        name: newActivityTitle,
        description: newActivityDescription,
        ownerInformation: {
          ownerName: newActivityOwnerName,
        },
        type: newActivityType[0] ? newActivityType[0] : null,
        responsible: responsible,
        biomassScreening: screeningData,
        fullBiomassScreeningEnabled,
        eudrReference,
        expireDate: newActivityExpireDate ? dayjs(newActivityExpireDate).format() : null,
        activityStatus: newActivityStatus,
        priority: 9999,
        correlationId,
      },
    });
    if (actId && actId !== "") {
      // Check for files and save these
      // Run through new files and save them with the individual categories form meta
      for (const file of newFiles) {
        await saveFiles(actId, selectedProjectId!, [file.file], file.metaData, correlationId);
      }
      onActivityCreated?.(actId);
    }
    handleClose();
  };

  async function saveFiles(
    activityId: string,
    projectId: string,
    files: File[],
    meta?: any,
    correlationId?: string,
  ) {
    if (files.length === 0 || activityId === "" || projectId === "") return;
    // Extract possible metadata from files
    const categories = meta?.categories || ["project", "activity", ...fileCategoryRef.current];
    if (categories.includes("completeDocument")) {
      enqueueSnackbar(translate("snackbar-screening-file-added"), {
        variant: "success",
      });
    }
    // Set file data
    const fileData: UploadFileEntryProps = {
      bucket: config.DEFAULT_FILES_BUCKET,
      categories: categories,
      uploadedBy: {
        id: user?.id,
        name: user?.firstName + " " + user?.lastName,
        email: user?.email,
        orgId: user?.orgId,
      },
      orgId: userOrg?.id || null,
      estateId: null,
      mapId: null,
      projectId: projectId,
      activityId: activityId,
      featureIds: null,
      featureCategories: null,
      featureRefLabels: [{ featureId: "", label: newActivityTitle }],
      correlationId,
    };
    if (!correlationId) {
      delete fileData.correlationId;
    }
    // Find root folder and root id
    const root = getRootFolderAndRootIdFromUploadFileProps(fileData);
    if (!root) {
      setFileError(translate("file-upload-error-generic"));
      return;
    }
    // Upload files
    uploadFiles({
      fileDoc: fileData,
      files: files,
      bucket: config.DEFAULT_FILES_BUCKET,
      rootFolder: root.rootFolder,
      rootId: root.rootId,
    });
  }

  const handleChangeActivity = (e: any, v: any) => {
    setDataChanged(true);
    if (v.length === 0) {
      setChosenActivity([]);
      setExistingActivitySectionOpen(false);
      setStateWithExistingData();
      return;
    }
    const lastItem = v.slice(-1)[0];
    // Set relevant state data
    setChosenActivity([lastItem]);
  };

  const handleSetNewActivitySectionOpen = () => {
    // Opening an empty section is navigation; dropping a selection is an edit.
    if (chosenActivity.length > 0) setDataChanged(true);
    if (chosenActivity.length > 0) setChosenActivity([]);
    setStateWithExistingData();
    setExistingActivitySectionOpen(false);
    setNewActivitySectionOpen(!newActivitySectionOpen);
  };

  // --- New Activity functions --- //
  const handleChangeNewActivityTitle = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDataChanged(true);
    setNewActivityTitle(e.target.value);
  };

  const handleChangeNewActivityDescription = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDataChanged(true);
    setNewActivityDescription(e.target.value);
  };

  const handleChangeOwnerName = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDataChanged(true);
    setNewActivityOwnerName(e.target.value);
  };

  const handleChangeEudrReference = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDataChanged(true);
    setEudrReference(e.target.value);
  };

  const handleChangeFullBiomassScreeningEnabled = (enabled: boolean) => {
    setDataChanged(true);
    setFullBiomassScreeningEnabled(enabled);
    if (!enabled) setTabValue(0);
  };

  const handleChangeNewActivityType = (e: SyntheticEvent<Element, Event>, v: any) => {
    setDataChanged(true);
    if (v.length === 0) {
      setNewActivityType([]);
      return;
    }
    const lastItem = v.slice(-1)[0];
    if (lastItem.group !== "logging" && updatedThinningDataRef.current.length > 0) {
      updatedThinningDataRef.current = [];
    }
    setNewActivityType([lastItem]);
  };

  const handleChangeNewActivityResponsible = (e: SyntheticEvent<Element, Event>, v: any) => {
    setDataChanged(true);
    if (v.length === 0) {
      setNewActivityResponsible([]);
      setNewActivitySendToResponsible(false);
      return;
    }
    const lastItem = v.slice(-1)[0];
    setNewActivityResponsible([lastItem]);
  };

  const handleContactFilter = (options: any, params: any) => {
    const filtered = filterContacts(options, params);
    const { inputValue } = params;
    // Check for existing option
    const isExisting = options.some((option: ContactType) => inputValue === option.email);
    // Check input to be email address
    const validEmail = validateEmail(inputValue);
    if (validEmail && !isExisting) {
      filtered.push({
        inputValue,
        email: inputValue,
        label: inputValue,
        name: inputValue,
        personType: "contact",
      });
    }
    return filtered;
  };

  const handleChangeActivityStatus = (e: SelectChangeEvent<string>) => {
    setDataChanged(true);
    setNewActivityStatus(e.target.value as DEFAULT_ACTIVITY_STATUS_TYPE);
  };

  const handleChangeTabValue = (event: React.SyntheticEvent, newValue: number) => {
    // setDataChanged(true);
    setTabValue(newValue);
  };

  const handleToggleDocking = (state: boolean) => {
    setMetaDataEditMode(state);
  };

  const handleSetUpdatedThinningData = (
    data: ActivityThinningObject[],
    type: "referenced" | "non-referenced",
    userChange = false,
  ) => {
    if (userChange) setDataChanged(true);
    if (data.length > 0 || userChange) {
      setLoadedThinningData(true);

      if (type === "referenced") {
        updatedThinningDataRef.current = data;
        const spcIds = data.map((el) => el.speciesId);
        handleChangeBiomassScreening({ value: spcIds, key: "species" }, userChange);
      } else {
        const spcIds = data.map((el) => el.speciesId);
        handleChangeBiomassScreening({ value: spcIds, key: "species" }, userChange);
      }
    }
  };

  const handleDeleteThinning = (thinningId: string, standId: string) => {
    setDataChanged(true);
    // Add stand id to list of removed stand ids
    setRemovedStandIds((prev) => [...prev, standId]);
    // Check if starts with new and if not delete it from firestore
    if (!thinningId.startsWith("new")) {
      deleteThinning(thinningId);
    }
  };

  const handleChangeBiomassScreening = ({
    value,
    key,
    parent,
  }: {
    value: any;
    key: string;
    parent?: string;
  }, userChange = true) => {
    // Initial thinning data is also reported here when the form/tab mounts.
    if (userChange) setDataChanged(true);
    setLoadedBiomassData(true);
    // }
    let updatedBiomassScreeningData = { ...biomassScreeningData } as any;
    snackbarChecker({
      key,
      value,
      parent,
      prevData: updatedBiomassScreeningData,
      largestArea: derivedActivityDataRef.current?.largestArea,
      translate,
      enqueueSnackbar,
    });
    if (parent) {
      let newVal = value;
      if (!updatedBiomassScreeningData[parent]) {
        updatedBiomassScreeningData[parent] = {};
      }
      // Set additional fields based on status
      if (parent === "status" && key === "protectedForest") {
        if (!updatedBiomassScreeningData["standAndActivity"]) {
          updatedBiomassScreeningData["standAndActivity"] = {};
          updatedBiomassScreeningData["standAndActivity"]["regeneration"] = value === "yes";
        } else if (!updatedBiomassScreeningData["standAndActivity"]["regeneration"]) {
          updatedBiomassScreeningData["standAndActivity"]["regeneration"] = value === "yes";
        } else {
          updatedBiomassScreeningData["standAndActivity"]["regeneration"] = value === "yes";
        }
      }
      if (parent === "standAndActivity") {
        if (key === "activity") {
          const hasThinningLikeActivity = hasThinningLikeIntervention(value);
          const hasClearcutLikeActivity = hasClearcutLikeIntervention(value);
          const hasFellingAndThinningActivity = hasFellingAndThinningIntervention(value);
          const hasClearingActivity = hasClearingIntervention(value);

          // Clear all standAndActivity data if no intervention is chosen
          if (value.length === 0) {
            updatedBiomassScreeningData["standAndActivity"] = { activity: [] };
          } else if (updatedBiomassScreeningData["category"] === "forest") {
            // Clear data if category is forest
            if (updatedBiomassScreeningData["standAndActivity"]["regeneration"] !== undefined) {
              if (!hasClearcutLikeActivity) {
                delete updatedBiomassScreeningData["standAndActivity"]["regeneration"];
              }
            }
            if (
              updatedBiomassScreeningData["standAndActivity"]["forestryEvaluation"]?.includes(
                "marking",
              )
            ) {
              if (!hasThinningLikeActivity) {
                updatedBiomassScreeningData["standAndActivity"]["forestryEvaluation"].splice(
                  updatedBiomassScreeningData["standAndActivity"]["forestryEvaluation"].indexOf(
                    "marking",
                  ),
                  1,
                );
              }
            }
            // Set data based on derived activity data
            if (hasClearcutLikeActivity) {
              if (!updatedBiomassScreeningData["standAndActivity"]["forestryEvaluation"]) {
                updatedBiomassScreeningData["standAndActivity"]["forestryEvaluation"] = [];
              }
              // If largest area > 5 ha set expert review to true
              if (
                derivedActivityDataRef.current?.largestArea &&
                derivedActivityDataRef.current.largestArea > 5 &&
                !updatedBiomassScreeningData["standAndActivity"]["forestryEvaluation"]?.includes(
                  "assessment",
                )
              ) {
                updatedBiomassScreeningData["standAndActivity"]["forestryEvaluation"].push(
                  "assessment",
                );
              }
            }
          } else if (updatedBiomassScreeningData["category"] === "agriculture") {
            delete updatedBiomassScreeningData["standAndActivity"]["regeneration"];
            if (updatedBiomassScreeningData["standAndActivity"]["appendix25Value"] !== undefined) {
              if (!hasFellingAndThinningActivity) {
                delete updatedBiomassScreeningData["standAndActivity"]["appendix25Value"];
              }
            }
          } else if (updatedBiomassScreeningData["category"] === "nonforest") {
            // Clear data if category is non forest
            if (updatedBiomassScreeningData["standAndActivity"]["regeneration"] !== undefined) {
              if (!hasClearingActivity) {
                delete updatedBiomassScreeningData["standAndActivity"]["regeneration"];
              }
            }
            if (updatedBiomassScreeningData["standAndActivity"]["appendix25Value"] !== undefined) {
              if (!hasFellingAndThinningActivity) {
                delete updatedBiomassScreeningData["standAndActivity"]["appendix25Value"];
              }
            }
          }
          // Init data
          if (
            updatedBiomassScreeningData["category"] === "nonforest" &&
            hasClearingActivity
          ) {
            if (!updatedBiomassScreeningData["standAndActivity"]["regeneration"]) {
              updatedBiomassScreeningData["standAndActivity"]["regeneration"] = false;
            }
          }
          if (updatedBiomassScreeningData["category"] === "forest" && hasThinningLikeActivity) {
            if (!updatedBiomassScreeningData["standAndActivity"]["expertReview"]) {
              updatedBiomassScreeningData["standAndActivity"]["expertReview"] = false;
            }
            if (!updatedBiomassScreeningData["standAndActivity"]["forestryEvaluation"]) {
              updatedBiomassScreeningData["standAndActivity"]["forestryEvaluation"] = [];
            }
          }
          if (updatedBiomassScreeningData["category"] === "forest" && hasClearcutLikeActivity) {
            if (!updatedBiomassScreeningData["standAndActivity"]["regeneration"]) {
              updatedBiomassScreeningData["standAndActivity"]["regeneration"] = false;
            }
            if (!updatedBiomassScreeningData["standAndActivity"]["expertReview"]) {
              updatedBiomassScreeningData["standAndActivity"]["expertReview"] = false;
            }
            if (!updatedBiomassScreeningData["standAndActivity"]["forestryEvaluation"]) {
              updatedBiomassScreeningData["standAndActivity"]["forestryEvaluation"] = [];
            }
            // Check for already set protected forest
            if (
              updatedBiomassScreeningData["status"] &&
              updatedBiomassScreeningData["status"]["protectedForest"] &&
              updatedBiomassScreeningData["status"]["protectedForest"] === "yes"
            ) {
              updatedBiomassScreeningData["standAndActivity"]["regeneration"] = true;
            }
          }
        } else if (key === "forestryEvaluation") {
          // If marking is chosen, thinning must be chosen as activity
          let newActivityValues: string[] = updatedBiomassScreeningData["standAndActivity"][
            "forestryEvaluation"
          ]
            ? updatedBiomassScreeningData["standAndActivity"]["forestryEvaluation"]
            : [];
          if (newActivityValues.includes(value)) {
            newActivityValues = newActivityValues.filter((el: string) => el !== value);
          } else {
            newActivityValues = [...newActivityValues, value];
          }
          newVal = newActivityValues;
        }
      }
      updatedBiomassScreeningData[parent][key] = newVal;
    } else {
      // If category changes reset state of other fields
      if (key === "category") {
        updatedBiomassScreeningData[key] = value;
        updatedBiomassScreeningData.type = null;
        updatedBiomassScreeningData.riscAssessment = [];
        updatedBiomassScreeningData.certificationNumber = null;
        updatedBiomassScreeningData.fscCertificationNumber = null;
        updatedBiomassScreeningData.pefcCertificationNumber = null;
        // Check for setting externally activated themes in map
        if (value === "forest") {
          setExternallyActivatedThemes(["screening-dk-forest"]);
        } else {
          setExternallyActivatedThemes(["screening-dk"]);
        }
        if (updatedBiomassScreeningData["status"]) {
          delete updatedBiomassScreeningData["status"];
        }
        if (updatedBiomassScreeningData["standAndActivity"]) {
          delete updatedBiomassScreeningData["standAndActivity"];
        }
        if (updatedBiomassScreeningData["natureAndConservation"]) {
          delete updatedBiomassScreeningData["natureAndConservation"];
        }
        if (updatedBiomassScreeningData["customerRequirement"]) {
          delete updatedBiomassScreeningData["customerRequirement"];
        }
        // Preset some states based on category type (only one choice in some states)
        if (value === "industryWaste") {
          updatedBiomassScreeningData.type = "cleanWaste";
          updatedBiomassScreeningData.riscAssessment = ["certified"];
        } else if (value === "infrastructure") {
          updatedBiomassScreeningData.type = "infrastructure";
          updatedBiomassScreeningData.riscAssessment = ["additionalInfo"];
        } else if (value === "nonforest") {
          updatedBiomassScreeningData.type = "residuesNonforest";
        }
      } else if (key === "type") {
        // Set additional fields based on type
        updatedBiomassScreeningData[key] = value;
        if (value === "clearcutEnergyWood") {
          if (!updatedBiomassScreeningData["standAndActivity"]) {
            updatedBiomassScreeningData["standAndActivity"] = {};
            updatedBiomassScreeningData["standAndActivity"]["activity"] = ["clearcut"];
          } else if (
            !updatedBiomassScreeningData["standAndActivity"]["activity"] ||
            !hasClearcutLikeIntervention(updatedBiomassScreeningData["standAndActivity"]["activity"]) ||
            hasThinningLikeIntervention(updatedBiomassScreeningData["standAndActivity"]["activity"])
          ) {
            updatedBiomassScreeningData["standAndActivity"]["activity"] = ["clearcut"];
          }
        } else if (
          value === "agricultureResidues" &&
          updatedBiomassScreeningData["category"] === "agriculture"
        ) {
          if (
            updatedBiomassScreeningData["riscAssessment"] &&
            updatedBiomassScreeningData["riscAssessment"].includes("ortho2008")
          ) {
            updatedBiomassScreeningData["riscAssessment"] = updatedBiomassScreeningData[
              "riscAssessment"
            ].filter((el: string) => el !== "ortho2008");
          }
        } else if (value === "energyWood") {
          if (
            updatedBiomassScreeningData["riscAssessment"] &&
            updatedBiomassScreeningData["riscAssessment"].includes("declarationPesticides")
          ) {
            updatedBiomassScreeningData["riscAssessment"] = updatedBiomassScreeningData[
              "riscAssessment"
            ].filter((el: string) => el !== "declarationPesticides");
          }
        }
      } else if (key === "riscAssessment") {
        // Set additional fields based on risc assessment
        if (updatedBiomassScreeningData.category === "agriculture") {
          let newRiscValues: string[] = updatedBiomassScreeningData.riscAssessment
            ? updatedBiomassScreeningData.riscAssessment
            : [];
          if (newRiscValues.includes(value)) {
            newRiscValues = newRiscValues.filter((el: string) => el !== value);
          } else {
            newRiscValues = [...newRiscValues, value];
          }
          updatedBiomassScreeningData.riscAssessment = newRiscValues;
        } else {
          updatedBiomassScreeningData.riscAssessment = [value];
          if (!updatedBiomassScreeningData["natureAndConservation"]) {
            updatedBiomassScreeningData["natureAndConservation"] = {};
          }
          if (value === "naturecareS3") {
            updatedBiomassScreeningData["natureAndConservation"]["nbl"] = "yes";
          } else if (value === "shelterbelt") {
            updatedBiomassScreeningData["natureAndConservation"]["nbl"] = null;
          }
        }
        if (value === "notCertified") {
          if (updatedBiomassScreeningData["type"] === "clearcutEnergyWood") {
            if (!updatedBiomassScreeningData["standAndActivity"]) {
              updatedBiomassScreeningData["standAndActivity"] = {};
              updatedBiomassScreeningData["standAndActivity"]["activity"] = ["clearcut"];
            } else if (
              !updatedBiomassScreeningData["standAndActivity"]["activity"] ||
              !hasClearcutLikeIntervention(updatedBiomassScreeningData["standAndActivity"]["activity"])
            ) {
              updatedBiomassScreeningData["standAndActivity"]["activity"] = ["clearcut"];
            }
          }
        }
      } else {
        updatedBiomassScreeningData[key] = value;
      }
    }
    if (
      activityConfig.isEnabled("infoBiomassScreeningEnableScreening") &&
      updatedBiomassScreeningData.riscAssessment?.includes("certified")
    ) {
      setFullBiomassScreeningEnabled(false);
      setTabValue(0);
    }

    // Update state
    setBiomassScreeningData(updatedBiomassScreeningData);
  };

  // --- Dynamic variables --- //
  let buttonText = translate("save");
  if (existingActivity) {
    if (existingActivity.id === "new") buttonText = translate("create");
  } else {
    if (withActivityCreate) {
      buttonText = translate("create");
    } else {
      buttonText = translate("add");
    }
  }

  let withLogging = false;
  let existingActivityId = "new";
  if (existingActivity) {
    existingActivityId = existingActivity.id;
  } else if (chosenActivity.length > 0) {
    existingActivityId = chosenActivity[0].activityId;
  }
  if (newActivityType.length > 0 && ["logging", "chipping"].includes(newActivityType[0].group)) {
    withLogging = true;
  }

  const activityProjectId = existingActivity?.projectId || selectedProjectId || undefined;
  const activityEstateData = getEstateDataFromProjectId(activityProjectId, projects, estates);
  selectedActivityEstateDataRef.current = activityEstateData;
  const activityCountry = activityEstateData?.country || "DK";
  const fullBiomassScreeningUnavailable =
    activityConfig.isEnabled("infoBiomassScreeningEnableScreening") &&
    biomassScreeningData?.riscAssessment?.includes("certified") === true;
  const interventionActivities = biomassScreeningData?.standAndActivity?.activity;
  const showRegenerationAgreement = shouldShowRegenerationAgreement(
    biomassScreeningData?.category,
    interventionActivities,
  );
  const activitySections = createActivityFormSections({
    config: activityConfig,
    country: activityCountry,
    withLogging,
    fullBiomassScreeningEnabled:
      fullBiomassScreeningEnabled && !fullBiomassScreeningUnavailable,
    labels: {
      information: translate("task-dialog-tab-information"),
      species: translate("task-dialog-tab-species"),
      screening: translate("task-dialog-tab-screening"),
    },
  });

  const activityReferences = (reduced: boolean) => (
    <ActivityReferencesSection
      translate={translate}
      reduced={reduced}
      withLogging={withLogging}
      disabled={activityConfig.isDisabled("species")}
      isSmallScreen={matchDownSM}
      activityId={existingActivityId}
      projectId={existingActivity?.projectId || selectedProjectId || "unknown"}
      references={newActivityRefs}
      nonReferencedSpecies={biomassScreeningData?.species}
      standData={standData}
      selectedFeature={selectedFeature}
      features={activityFeatures}
      thinningData={thinningData}
      removedStandIds={removedStandIds}
      onUpdatedThinningData={handleSetUpdatedThinningData}
      onDeleteThinning={handleDeleteThinning}
    />
  );

  // --- Additional JSX --- //
  const informationContent = (withLogging: boolean, existingActivityId: string) => {
    const hasConfiguredInformationLayout = !!appConfig.informationTab?.elements;

    if (hasConfiguredInformationLayout) {
      const screeningDisabled =
        activityConfig.isDisabled("changeScreeningData") ||
        (existingActivity !== undefined &&
          user?.id !== undefined &&
          ["viewer", "buyer"].includes(getUserProjectRole(user.id, existingActivity.projectId)));
      const inputDisabled = screeningDisabled;
      const informationElements = getActivityInformationElements(appConfig.informationTab?.elements);

      const basicFields = (
        elements: Array<"title" | "description" | "type">,
        showDescriptionHeading = false,
      ) => (
        <ActivityBasicInformationFields
          config={activityConfig}
          translate={translate}
          title={newActivityTitle}
          description={newActivityDescription}
          ownerName={newActivityOwnerName}
          activityTypes={ACTIVITY_TYPES}
          selectedActivityType={newActivityType}
          titleError={submitError !== null && newActivityTitle === ""}
          onTitleChange={handleChangeNewActivityTitle}
          onDescriptionChange={handleChangeNewActivityDescription}
          onOwnerNameChange={handleChangeOwnerName}
          onActivityTypeChange={handleChangeNewActivityType}
          elements={elements}
          showDescriptionHeading={showDescriptionHeading}
        />
      );

      const assignmentFields = (elements: Array<"responsibility" | "scheduleStatus" | "eudrReference" | "activityId">) => (
        <ActivityAssignmentAndStatusFields
          config={activityConfig}
          translate={translate}
          responsibleOptions={RESPONSIBLE_DATA}
          responsible={newActivityResponsible}
          sendToResponsible={newActivitySendToResponsible}
          expireDate={newActivityExpireDate}
          requireExpireDate={requireExpireDate}
          status={newActivityStatus}
          statusOptions={ACTIVITY_STATUS}
          submitError={submitError}
          existingActivityId={existingActivity?.id}
          withLogging={withLogging}
          eudrReference={eudrReference}
          screeningDisabled={screeningDisabled}
          onResponsibleChange={handleChangeNewActivityResponsible}
          onResponsibleFilter={handleContactFilter}
          onSendToResponsibleToggle={() => {
            setDataChanged(true);
            setNewActivitySendToResponsible(!newActivitySendToResponsible);
          }}
          onExpireDateChange={(date) => {
            if (date?.valueOf() !== newActivityExpireDate?.valueOf()) setDataChanged(true);
            setNewActivityExpireDate(date);
          }}
          onStatusChange={handleChangeActivityStatus}
          onEudrReferenceChange={handleChangeEudrReference}
          elements={elements}
        />
      );

      const biomassBlock =
        withLogging &&
        (activityConfig.isVisible("infoBiomassScreeningCategory") ||
          activityConfig.isVisible("infoBiomassScreeningIntervention")) && (
          <>
            <Grid item xs={12}><Divider /></Grid>
            <Grid item xs={12}>
              <Typography variant="h5" sx={{ mb: -2 }}>{translate("biomass-project-form-biomass")}</Typography>
            </Grid>
            <BiomassScreeningCoreFields
              data={biomassScreeningData}
              setData={handleChangeBiomassScreening}
              inputDisabled={inputDisabled}
              visibleFields={{
                category: activityConfig.isVisible("infoBiomassScreeningCategory"),
                intervention: activityConfig.isVisible("infoBiomassScreeningIntervention"),
                riskAssessment: activityConfig.isVisible("infoBiomassScreeningRiskAssessment"),
              }}
              showCertificateFields={hasBiomassScreeningCertificateFields(biomassScreeningData)}
            />
            <BiomassScreeningCertificateFields
              data={biomassScreeningData}
              setData={handleChangeBiomassScreening}
              hasError={submitError === "activity-certificate-number-required" && !hasBiomassScreeningCertificationNumber(biomassScreeningData)}
              disabled={inputDisabled}
            />
          </>
        );

      const interventionBlock =
        withLogging &&
        activityConfig.isVisible("infoBiomassScreeningIntervention") &&
        biomassScreeningData?.category &&
        [BIOMASS_CATEGORIES[0], BIOMASS_CATEGORIES[1], BIOMASS_CATEGORIES[4]].includes(biomassScreeningData.category) && (
          <>
            <Grid item xs={12}><Divider /></Grid>
            <Grid item xs={12}>
              <Typography variant="h5" sx={{ mb: -1 }}>{translate("biomass-project-form-harvest-category")}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ bgcolor: "secondary.lighter", p: 2, borderRadius: 1 }}>
                <Grid container spacing={{ xs: 2 }}>
                  <BiomassScreeningInterventionField
                    data={biomassScreeningData}
                    inputDisabled={inputDisabled}
                    derivedActivityData={derivedActivityDataRef.current}
                    gridColumns={4}
                    setData={handleChangeBiomassScreening}
                  />
                  {showRegenerationAgreement && (
                    <Grid item xs={12} sm={8}>
                      <Box sx={{ display: "flex", alignItems: "stretch" }}>
                        <Box sx={{ mr: 1, alignSelf: "stretch" }}><ToolbarDivider orientation="horizontal" /></Box>
                        <RegenerationAgreementField
                          checked={biomassScreeningData?.standAndActivity?.regeneration ?? false}
                          agreementRequired={biomassScreeningData?.status?.protectedForest === "yes"}
                          inputDisabled={inputDisabled}
                          loading={loading}
                          onChange={handleChangeBiomassScreening}
                          onUpload={handleOpenFilePicker}
                        />
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Box>
            </Grid>
          </>
        );

      const blocks = {
        title: () => basicFields(["title"]),
        description: () =>
          basicFields(
            ["description"],
            appConfig.informationTab?.elements?.description?.options?.showHeading,
          ),
        type: () => basicFields(["type"]),
        biomass: () => biomassBlock,
        intervention: () => interventionBlock,
        references: () => activityConfig.isVisible("references") && <><Grid item xs={12}><Divider /></Grid>{activityReferences(true)}</>,
        responsibility: () => assignmentFields(["responsibility"]),
        scheduleStatus: () => assignmentFields(["scheduleStatus"]),
        eudrReference: () => assignmentFields(["eudrReference"]),
        screeningActivation: () => <ActivityScreeningActivationFields config={activityConfig} translate={translate} withLogging={withLogging} fullBiomassScreeningEnabled={fullBiomassScreeningEnabled} fullBiomassScreeningUnavailable={fullBiomassScreeningUnavailable} disabled={screeningDisabled} onFullBiomassScreeningEnabledChange={handleChangeFullBiomassScreeningEnabled} />,
        activityId: () => assignmentFields(["activityId"]),
      };
      const blockIsVisible = {
        title: activityConfig.isVisible("title"),
        description: activityConfig.isVisible("description"),
        type: activityConfig.isVisible("type"),
        biomass: !!biomassBlock,
        intervention: !!interventionBlock,
        references: activityConfig.isVisible("references"),
        responsibility:
          activityConfig.isVisible("responsible") || activityConfig.isVisible("notifyResponsible"),
        scheduleStatus: activityConfig.isVisible("expireDate") || activityConfig.isVisible("status"),
        eudrReference: withLogging && activityConfig.isVisible("eudrReference"),
        screeningActivation:
          withLogging && activityConfig.isVisible("infoBiomassScreeningEnableScreening"),
        activityId: !!existingActivity?.id && existingActivity.id !== "new",
      };
      const visibleInformationElements = informationElements.filter(
        (element) => blockIsVisible[element.id],
      );

      return (
        <Grid container rowSpacing={3} columnSpacing={2} sx={{ mb: 2 }}>
          {visibleInformationElements.map((element, index) => (
            <Fragment key={element.id}>
              {element.options?.dividerBefore && index > 0 && (
                <Grid item xs={12}>
                  <Divider />
                </Grid>
              )}
              {blocks[element.id]()}
            </Fragment>
          ))}
        </Grid>
      );
    }

    return (
      <Grid container rowSpacing={3} columnSpacing={2} sx={{ mb: 2 }}>
        <ActivityBasicInformationFields
          config={activityConfig}
          translate={translate}
          title={newActivityTitle}
          description={newActivityDescription}
          ownerName={newActivityOwnerName}
          activityTypes={ACTIVITY_TYPES}
          selectedActivityType={newActivityType}
          titleError={submitError !== null && newActivityTitle === ""}
          onTitleChange={handleChangeNewActivityTitle}
          onDescriptionChange={handleChangeNewActivityDescription}
          onOwnerNameChange={handleChangeOwnerName}
          onActivityTypeChange={handleChangeNewActivityType}
        />

        {withLogging &&
          (activityConfig.isVisible("infoBiomassScreeningCategory") ||
            activityConfig.isVisible("infoBiomassScreeningIntervention")) && (
            <>
              <Grid item xs={12}>
                <Divider />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h5" sx={{ mb: -2 }}>
                  {translate("biomass-project-form-biomass")}
                </Typography>
              </Grid>
              <BiomassScreeningCoreFields
                data={biomassScreeningData}
                setData={handleChangeBiomassScreening}
                inputDisabled={
                  activityConfig.isDisabled("changeScreeningData") ||
                  (existingActivity !== undefined &&
                    user?.id !== undefined &&
                    ["viewer", "buyer"].includes(
                      getUserProjectRole(user.id, existingActivity.projectId),
                    ))
                }
                visibleFields={{
                  category: activityConfig.isVisible("infoBiomassScreeningCategory"),
                  intervention: activityConfig.isVisible("infoBiomassScreeningIntervention"),
                  riskAssessment: activityConfig.isVisible("infoBiomassScreeningRiskAssessment"),
                }}
                showCertificateFields={hasBiomassScreeningCertificateFields(biomassScreeningData)}
              />
              <BiomassScreeningCertificateFields
                data={biomassScreeningData}
                setData={handleChangeBiomassScreening}
                hasError={
                  submitError === "activity-certificate-number-required" &&
                  !hasBiomassScreeningCertificationNumber(biomassScreeningData)
                }
                disabled={
                  activityConfig.isDisabled("changeScreeningData") ||
                  (existingActivity !== undefined &&
                    user?.id !== undefined &&
                    ["viewer", "buyer"].includes(
                      getUserProjectRole(user.id, existingActivity.projectId),
                    ))
                }
              />
              {activityConfig.isVisible("infoBiomassScreeningIntervention") &&
                biomassScreeningData?.category &&
                [BIOMASS_CATEGORIES[0], BIOMASS_CATEGORIES[1], BIOMASS_CATEGORIES[4]].includes(
                  biomassScreeningData.category,
                ) && (
                  <>
                    <Grid item xs={12}>
                      <Divider />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="h5" sx={{ mb: -1 }}>
                        {translate("biomass-project-form-harvest-category")}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={{ bgcolor: "secondary.lighter", p: 2, borderRadius: 1 }}>
                        <Grid container spacing={{ xs: 2 }}>
                          <BiomassScreeningInterventionField
                            data={biomassScreeningData}
                            inputDisabled={
                              activityConfig.isDisabled("changeScreeningData") ||
                              (existingActivity !== undefined &&
                                user?.id !== undefined &&
                                ["viewer", "buyer"].includes(
                                  getUserProjectRole(user.id, existingActivity.projectId),
                                ))
                            }
                            derivedActivityData={derivedActivityDataRef.current}
                            gridColumns={4}
                            setData={handleChangeBiomassScreening}
                          />
                          {showRegenerationAgreement && (
                            <Grid item xs={12} sm={8}>
                              <Box sx={{ display: "flex", alignItems: "stretch" }}>
                                <Box sx={{ mr: 1, alignSelf: "stretch" }}>
                                  <ToolbarDivider orientation="horizontal" />
                                </Box>
                                <RegenerationAgreementField
                                  checked={biomassScreeningData?.standAndActivity?.regeneration ?? false}
                                  agreementRequired={biomassScreeningData?.status?.protectedForest === "yes"}
                                  inputDisabled={
                                    activityConfig.isDisabled("changeScreeningData") ||
                                    (existingActivity !== undefined &&
                                      user?.id !== undefined &&
                                      ["viewer", "buyer"].includes(
                                        getUserProjectRole(user.id, existingActivity.projectId),
                                      ))
                                  }
                                  loading={loading}
                                  onChange={handleChangeBiomassScreening}
                                  onUpload={handleOpenFilePicker}
                                />
                              </Box>
                            </Grid>
                          )}
                        </Grid>
                      </Box>
                    </Grid>
                  </>
                )}
            </>
          )}

        {/* Link and thinning */}
        {activityConfig.isVisible("references") && (
          <>
            <Grid item xs={12}>
              <Divider />
            </Grid>

            {activityReferences(true)}
          </>
        )}

        <ActivityAssignmentAndStatusFields
          config={activityConfig}
          translate={translate}
          responsibleOptions={RESPONSIBLE_DATA}
          responsible={newActivityResponsible}
          sendToResponsible={newActivitySendToResponsible}
          expireDate={newActivityExpireDate}
          requireExpireDate={requireExpireDate}
          status={newActivityStatus}
          statusOptions={ACTIVITY_STATUS}
          submitError={submitError}
          existingActivityId={existingActivity?.id}
          withLogging={withLogging}
          eudrReference={eudrReference}
          screeningDisabled={
            activityConfig.isDisabled("changeScreeningData") ||
            (existingActivity !== undefined &&
              user?.id !== undefined &&
              ["viewer", "buyer"].includes(
                getUserProjectRole(user.id, existingActivity.projectId),
              ))
          }
          screeningActivation={
            <ActivityScreeningActivationFields
              config={activityConfig}
              translate={translate}
              withLogging={withLogging}
              fullBiomassScreeningEnabled={fullBiomassScreeningEnabled}
              fullBiomassScreeningUnavailable={fullBiomassScreeningUnavailable}
              disabled={
                activityConfig.isDisabled("changeScreeningData") ||
                (existingActivity !== undefined &&
                  user?.id !== undefined &&
                  ["viewer", "buyer"].includes(
                    getUserProjectRole(user.id, existingActivity.projectId),
                  ))
              }
              onFullBiomassScreeningEnabledChange={handleChangeFullBiomassScreeningEnabled}
            />
          }
          onResponsibleChange={handleChangeNewActivityResponsible}
          onResponsibleFilter={handleContactFilter}
          onSendToResponsibleToggle={() => {
            setDataChanged(true);
            setNewActivitySendToResponsible(!newActivitySendToResponsible);
          }}
          onExpireDateChange={(date) => {
            if (date?.valueOf() !== newActivityExpireDate?.valueOf()) setDataChanged(true);
            setNewActivityExpireDate(date);
          }}
          onStatusChange={handleChangeActivityStatus}
          onEudrReferenceChange={handleChangeEudrReference}
        />
      </Grid>
    );
  };

  const speciesContent = (withLogging: boolean, existingActivityId: string) => {
    return (
      <Box sx={{ mt: -3, minHeight: 260 }}>
        <Grid container rowSpacing={3} columnSpacing={2} sx={{ mb: 2 }}>
          {/* Link and thinning */}
          {activityReferences(false)}
        </Grid>
      </Box>
    );
  };

  const screeningContent = () => {
    if (!user || !user.id) return null;
    // Set additional form data
    let addFormEstateStr = "-";
    if (activityConfig.isEnabled("owner")) {
      addFormEstateStr = newActivityOwnerName !== "" ? newActivityOwnerName : "-";
    } else {
      addFormEstateStr = getEstateDataAsStr(selectedActivityEstateDataRef.current, translate);
    }
    // Get project data
    const projectData = getProjectDataFromProjectId(
      existingActivity?.projectId || selectedProjectId || "",
      projects,
    );
    // Check for disabled input in screening based on config and project role
    const prjRole = getUserProjectRole(
      user.id,
      existingActivity?.projectId || selectedProjectId || "",
    );
    const isInputDisabled =
      activityConfig.isDisabled("changeScreeningData") || ["viewer", "buyer"].includes(prjRole);
    return (
      <ActivityScreeningSection
        biomassProjectFormProps={{
          withDocking,
          inputDisabled: isInputDisabled,
          loading,
          existingActivity,
          biomassScreeningData,
          derivedActivityData: derivedActivityDataRef.current,
          additionalFormData: {
            responsible:
              newActivityResponsible.length > 0 ? newActivityResponsible[0].name || "-" : "-",
            estate: addFormEstateStr,
            company: getUserOrgDataAsStr(userOrg, translate),
            projectTitle: projectData ? projectData.name : "-",
            taskTitle: newActivityTitle,
            taskID: existingActivity?.id && existingActivity.id !== "new" ? existingActivity.id : "-",
            note: newActivityDescription || "-",
            species: biomassScreeningData?.species,
            estateType: newActivityOwnerName !== "" ? "string" : "contact",
          },
          existingFiles: activitySpecificFiles,
          newFiles,
          setBiomassScreeningData: handleChangeBiomassScreening,
          setOpenFilePickerModal: handleOpenFilePicker,
          onSaveFiles: saveFiles,
          onChangeFile: handleChangeFile,
          onDownloadFile: handleDownloadFile,
          onDeleteFile: handleOpenFileConfirmDialog,
          onDeleteNewFile: handleDeleteNewFile,
          setParentDisableState: setIsDisabled,
        }}
      />
    );
  };

  const activityContent = () => {
    const contentBySection = {
      information: informationContent(withLogging, existingActivityId),
      species: speciesContent(withLogging, existingActivityId),
      screening: screeningContent(),
    };

    return (
      <>
        {activitySections.map((section, index) => (
          <TabPanel key={section.id} value={tabValue} index={index} withBox boxStyle={{ pt: 0 }}>
            {contentBySection[section.id]}
          </TabPanel>
        ))}
      </>
    );
  };

  // --- Render --- //
  return (
    <>
      {!existingActivity && (
        <ProjectChooseOrCreate
          openExt={openChooseProject}
          setOpenExt={setOpenChooseProject}
          withProjectCreate={withProjectCreate}
          signalCancel={handleSignalCancel}
          hideBackdrop={false}
        />
      )}

      {!openChooseProject && (
        <SingleColumnInput
          title={translate("activity")}
          tabs={
            <ActivityFormTabs
              sections={activitySections}
              value={tabValue}
              onChange={handleChangeTabValue}
              tabSx={style}
            />
          }
          maxWidth="sm"
          sectionHeader={
            existingActivity
              ? existingActivity.id === "new"
                ? translate("create")
                : translate("change")
              : translate("activity-choose-or-open-dialog-header-text")
          }
          open={openExt}
          loading={loading}
          saveDisabled={!activityConfig.isEnabled("saveButton") || isDisabled}
          cancelDisabled={isDisabled}
          handleClose={handleCloseInput}
          handleSave={handleSave}
          errorText={submitError ? translate(submitError) : undefined}
          buttonText={buttonText}
          allowDocking={withDocking}
          toggleDocking={handleToggleDocking}
        >
          {
            <Grid container spacing={{ xs: 2 }}>
              {/* ------ Section for existing activities via the map ------ */}
              {!existingActivity && tabValue === 0 && (
                <Grid item xs={12}>
                  <Box sx={{ pt: 2 }}>
                    <AutoCompleteGrouped
                      title="title"
                      label={translate("choose-activity")}
                      showTitle={false}
                      showLabel={false}
                      data={options}
                      groupByField={groupByRef.current ? "estateName" : undefined}
                      renderOptionField={"activityName"}
                      renderTagField={"activityName"}
                      noOptionsText={translate("no-options")}
                      changeHandler={handleChangeActivity}
                      value={chosenActivity}
                      multiple={true}
                    />
                  </Box>
                </Grid>
              )}

              {existingActivitySectionOpen && tabValue === 0 && (
                <Grid item xs={12}>
                  <Divider sx={{ mt: 1, mb: 1 }} />
                </Grid>
              )}

              <Grid item xs={12}>
                <Collapse
                  in={existingActivitySectionOpen}
                  timeout="auto"
                  unmountOnExit
                  sx={{ pt: 2 }}
                >
                  {activityContent()}
                </Collapse>
              </Grid>

              {/* ------ Section for new activities ------ */}
              {withActivityCreate && (
                <>
                  {!existingActivity && tabValue === 0 && (
                    <>
                      <Grid item xs={12}>
                        <Divider>{translate("or")}</Divider>
                      </Grid>

                      {!newActivitySectionOpen && (
                        <Grid item xs={12}>
                          <Link
                            onClick={() => handleSetNewActivitySectionOpen()}
                            underline="always"
                            style={{ cursor: "pointer" }}
                          >
                            {translate("create-new-activity")}
                          </Link>
                        </Grid>
                      )}

                      {newActivitySectionOpen && (
                        <Grid item xs={12}>
                          <Box sx={{ mb: 0 }} />
                        </Grid>
                      )}
                    </>
                  )}

                  <Grid item xs={12}>
                    <Collapse in={newActivitySectionOpen} timeout="auto" unmountOnExit>
                      {activityContent()}
                    </Collapse>
                  </Grid>
                </>
              )}
            </Grid>
          }
        </SingleColumnInput>
      )}
      <ConfirmDialog
        open={openFileConfirmDialog}
        matchDownMD={true}
        title={translate("permanent-loss-of-file-title")}
        text={translate("permanent-loss-of-file-text")}
        onConfirmText={translate("cancel")}
        onConfirm={handleCancelFileConfirmDialog}
        onCancelText={translate("loss-of-data-proceed")}
        onCancel={handleConfirmFileConfirmDialog}
      />
      <ConfirmDialog
        open={openCancelConfirmDialog}
        matchDownMD={true}
        title={translate("loss-of-data-title")}
        text={translate("loss-of-data-text")}
        onConfirmText={translate("loss-of-data-cancel")}
        onConfirm={handleCancelConfirmDialog}
        onCancelText={translate("loss-of-data-proceed")}
        onCancel={handleConfirmDialog}
      />
    </>
  );
};

export default ActivityChooseOrCreate;
